/**
 * The measured input latency, per input device.
 *
 * Stored against the device LABEL, because that is what it is a fact about: a user who calibrated
 * their audio interface has said nothing about the laptop's built-in microphone, and a tool that
 * carried the number across would keep subtracting the interface's delay from a performance the
 * built-in microphone captured - silently mis-scoring exactly when the setup changed. Since the
 * trainer re-offers calibration whenever the label changes, keying on the label is also what makes
 * "offered again when the device label changes" (§6.3) fall out for free.
 *
 * A device the browser has not named yet - which is every device before the first permission grant
 * - is never treated as calibrated and is never stored. The first take on a machine therefore
 * always measures, which is the right way round for a number that is then subtracted from every
 * onset the player produces.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.3
 */

/**
 * All three §6.3 tap sources - a sung "ta", a MIDI note, a keyboard key - are filed under the
 * TAKE's device label, including the keyboard.
 *
 * The quantity being measured is the whole loop: output buffer, then the player's reaction, then
 * the input path. The first two terms dominate and are shared by every source; what a keyboard tap
 * leaves out is the microphone's own conversion, which on current hardware is a few milliseconds
 * and is replaced by the key's scan latency anyway. Filing a keyboard measurement separately would
 * make it useless - it would never unblock the mode the player is actually about to use - which is
 * a worse outcome than a measurement that is a few ms optimistic.
 */
export const getInputLatency = (
  byDevice: Record<string, number> | undefined,
  deviceLabel: string | null
): number | null => {
  if (!deviceLabel) {
    return null
  }
  /*
   * `?? null` and never `|| null`: a device really can measure 0 ms, and a falsy check would
   * report the fastest possible setup as uncalibrated and refuse to score its timing.
   */
  return byDevice?.[deviceLabel] ?? null
}

export const withInputLatency = (
  byDevice: Record<string, number> | undefined,
  deviceLabel: string | null,
  inputLatencyMs: number
): Record<string, number> => {
  const current = byDevice ?? {}
  if (!deviceLabel) {
    return current
  }
  return { ...current, [deviceLabel]: inputLatencyMs }
}
